import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import "../POS/PurchaseOrders.css";
import PoPrintTemplate from "../POS/PoPrintTemplate";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import apiService, { getItems, getSuppliers } from "../../services/apiService";

export default function PurchaseOrders() {
  const [poList, setPoList] = useState([]);
  const [selectedPO, setSelectedPO] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);

  const [poPage, setPoPage] = useState(1);
  const PO_PAGE_SIZE = 5;

  const [showCreateModal, setShowCreateModal] = useState(false);

  const initialPoHeader = {
    PONumber: "",
    PRNo: "",
    PRDate: "",
    Supplier: null,
    OrderDate: "",
    RequestDeliveryDate: "",
    Status: "Pending",
    Email: "",
    Phone: "",
    Attn: "",
    Currency: "PHP",
    PaymentTerms: "",
    DeliveryTerms: "",
    ModeOfTransport: "",
    ShipVia: "",
    Comments: "",
    items: [],
  };

  const [poHeader, setPoHeader] = useState(initialPoHeader);

  const [itemForm, setItemForm] = useState({
    ItemId: "",
    CatalogNo: "",
    Description: "",
    OrderedQty: "",
    UnitPrice: "",
    ReceivedQty: 0,
    PendingQty: 0,
    Status: "Pending",
  });

  const [editIndex, setEditIndex] = useState(null);

  const poStartedRef = useRef(false);

  const paymentTermOptions = [
    { value: "30 Days", label: "30 Days" },
    { value: "60 Days", label: "60 Days" },
    { value: "90 Days", label: "90 Days" },
  ];

  const deliveryTermOptions = [
    { value: "Ex-Works", label: "Ex-Works" },
    { value: "FOB", label: "FOB" },
    { value: "CIF", label: "CIF" },
  ];

  const modeOfTransportOptions = [
    { value: "Airfreight", label: "Airfreight" },
    { value: "Seafreight", label: "Seafreight" },
    { value: "Courier", label: "Courier" },
  ];

  const shipViaOptions = [
    { value: "DHL Express", label: "DHL Express" },
    { value: "FedEx", label: "FedEx" },
    { value: "UPS", label: "UPS" },
  ];

  useEffect(() => {
    poStartedRef.current =
      !!poHeader.Supplier ||
      !!poHeader.PRNo ||
      !!poHeader.PRDate ||
      !!poHeader.OrderDate ||
      !!poHeader.RequestDeliveryDate ||
      (poHeader.items?.length || 0) > 0;
  }, [
    poHeader.Supplier,
    poHeader.PRNo,
    poHeader.PRDate,
    poHeader.OrderDate,
    poHeader.RequestDeliveryDate,
    poHeader.items,
  ]);

  useEffect(() => {
    let alive = true;

    const fetchData = async () => {
      try {
        const supplierData = await getSuppliers();
        const itemData = await getItems();
        const poData = await apiService.getPurchaseOrders();

        const mergedPOs = await Promise.all(
          (poData || []).map(async (po) => {
            const supplier = (supplierData || []).find(
              (s) => Number(s.SupplierID) === Number(po.SupplierID)
            );

            const poDetails = await apiService.getPurchaseOrder(po.POID);

            return {
              ...po,
              ...supplier,
              SupplierName:
                po.SupplierName ||
                supplier?.SupplierName ||
                supplier?.Name ||
                "",
              items: poDetails?.items || [],
            };
          })
        );

        if (!alive) return;

        setSuppliers(supplierData || []);
        setItems(itemData || []);
        setPoList(mergedPOs || []);
      } catch (error) {
        console.error("Error loading data:", error);
      }
    };

    fetchData();

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    let timerId = null;

    const loop = async () => {
      if (!alive) return;

      try {
        if (!poStartedRef.current) {
          const { PONumber } = await apiService.getNextPONumber();
          if (!alive) return;

          setPoHeader((prev) => {
            const started =
              !!prev.Supplier ||
              !!prev.PRNo ||
              !!prev.PRDate ||
              !!prev.OrderDate ||
              !!prev.RequestDeliveryDate ||
              (prev.items?.length || 0) > 0;

            if (started) return prev;

            return {
              ...prev,
              PONumber,
            };
          });
        }
      } catch (error) {
        console.error("Failed to fetch next PO number:", error);
      }

      timerId = setTimeout(loop, 10000);
    };

    loop();

    return () => {
      alive = false;
      if (timerId) clearTimeout(timerId);
    };
  }, []);

  useEffect(() => {
    let alive = true;
    let timerId = null;

    const loop = async () => {
      if (!alive) return;

      if (!selectedPO && !document.hidden) {
        try {
          const poData = await apiService.getPurchaseOrders();

          const merged = (poData || []).map((po) => {
            const supplier = suppliers.find(
              (s) => Number(s.SupplierID) === Number(po.SupplierID)
            );

            return {
              ...po,
              SupplierName:
                po.SupplierName ||
                supplier?.SupplierName ||
                supplier?.Name ||
                "",
            };
          });

          if (alive) setPoList(merged);
        } catch (e) {
          console.error("PO polling failed:", e);
        }
      }

      timerId = setTimeout(loop, 10000);
    };

    loop();

    return () => {
      alive = false;
      if (timerId) clearTimeout(timerId);
    };
  }, [selectedPO, suppliers]);

  const totalPages = Math.max(1, Math.ceil((poList?.length || 0) / PO_PAGE_SIZE));

  useEffect(() => {
    setPoPage((p) => Math.min(Math.max(1, p), totalPages));
  }, [totalPages]);

  const pagedPOs = (poList || []).slice(
    (poPage - 1) * PO_PAGE_SIZE,
    poPage * PO_PAGE_SIZE
  );

  const supplierOptions = suppliers.map((s) => ({
    value: s.SupplierID,
    label: s.SupplierName || s.Name,
    supplier: s,
  }));

  const handleSupplierChange = (selected) => {
    const supplier = selected ? selected.supplier : null;

    setPoHeader((prev) => ({
      ...prev,
      Supplier: supplier,
      Email: supplier?.Email || "",
      Phone: supplier?.Phone || "",
      Attn: supplier?.ContactPerson || "",
      Currency: supplier?.Currency || "PHP",
    }));
  };

  const formatCurrencyTotal = (amount) => {
    const currency = poHeader.Currency || poHeader.Supplier?.Currency || "PHP";

    return `${currency} ${Number(amount || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const filteredItems = items.filter((it) => {
    const matchesSearch =
      (it.CatalogNo || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (it.ItemName || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (it.Description || "").toLowerCase().includes(searchTerm.toLowerCase());

    const matchesSupplier = poHeader.Supplier?.SupplierID
      ? Number(it.SupplierID) === Number(poHeader.Supplier.SupplierID)
      : true;

    return matchesSearch && matchesSupplier;
  });

  const handleItemSelect = (e) => {
    const selectedId = e.target.value;

    const selected = items.find(
      (i) => Number(i.ItemId || i.ItemID) === Number(selectedId)
    );

    if (selected) {
      setItemForm((prev) => ({
        ...prev,
        ItemId: selected.ItemId || selected.ItemID,
        CatalogNo: selected.CatalogNo || "",
        Description: selected.Description || selected.ItemName || "",
        UnitPrice: selected.POPrice ?? "",
      }));
    } else {
      setItemForm((prev) => ({
        ...prev,
        ItemId: "",
        CatalogNo: "",
        Description: "",
        UnitPrice: "",
      }));
    }
  };

  const handleQtyChange = (e) => {
    setItemForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const resetItemForm = () => {
    setItemForm({
      ItemId: "",
      CatalogNo: "",
      Description: "",
      OrderedQty: "",
      UnitPrice: "",
      ReceivedQty: 0,
      PendingQty: 0,
      Status: "Pending",
    });

    setEditIndex(null);
    setSearchTerm("");
  };

  const addLineItem = (e) => {
    if (e) e.preventDefault();

    if (!itemForm.ItemId || !itemForm.OrderedQty || !itemForm.UnitPrice) return;

    const orderedQty = parseInt(itemForm.OrderedQty, 10);
    const unitPrice = parseFloat(itemForm.UnitPrice);

    const newItem = {
      ...itemForm,
      PODetailID:
        editIndex !== null
          ? poHeader.items[editIndex].PODetailID
          : poHeader.items.length + 1,
      OrderedQty: orderedQty,
      UnitPrice: unitPrice,
      ExtendedPrice: orderedQty * unitPrice,
      ReceivedQty: 0,
      PendingQty: orderedQty,
      Status: "Pending",
    };

    let updatedItems;

    if (editIndex !== null) {
      updatedItems = [...poHeader.items];
      updatedItems[editIndex] = newItem;
    } else {
      updatedItems = [...poHeader.items, newItem];
    }

    setPoHeader((prev) => ({
      ...prev,
      items: updatedItems,
    }));

    resetItemForm();
  };

  const editLineItem = (index) => {
    const item = poHeader.items[index];

    setItemForm({
      ItemId: item.ItemId,
      CatalogNo: item.CatalogNo,
      Description: item.Description,
      OrderedQty: item.OrderedQty,
      UnitPrice: item.UnitPrice,
      ReceivedQty: item.ReceivedQty,
      PendingQty: item.PendingQty,
      Status: item.Status,
    });

    setEditIndex(index);
  };

  const deleteLineItem = (index) => {
    const updatedItems = poHeader.items.filter((_, i) => i !== index);

    setPoHeader((prev) => ({
      ...prev,
      items: updatedItems,
    }));

    if (editIndex === index) {
      resetItemForm();
    }
  };

  const resetPoForm = async () => {
    try {
      const nxt = await apiService.getNextPONumber();

      setPoHeader({
        ...initialPoHeader,
        PONumber: nxt?.PONumber || "",
      });
    } catch {
      setPoHeader(initialPoHeader);
    }

    resetItemForm();
  };

  const openCreateModal = async () => {
    await resetPoForm();
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    resetItemForm();
  };

  const closeViewModal = () => {
    setSelectedPO(null);
  };

  const savePO = async (e) => {
    e.preventDefault();

    if (
      !poHeader.Supplier ||
      !poHeader.OrderDate ||
      !poHeader.RequestDeliveryDate ||
      poHeader.items.length === 0
    ) {
      alert("Please complete PO Header and add at least one item.");
      return;
    }

    const payload = {
      PONumber: poHeader.PONumber,
      PRNo: poHeader.PRNo,
      PRDate: poHeader.PRDate,
      SupplierID: poHeader.Supplier.SupplierID,
      OrderDate: poHeader.OrderDate,
      RequestDeliveryDate: poHeader.RequestDeliveryDate,
      PaymentTerms: poHeader.PaymentTerms,
      DeliveryTerms: poHeader.DeliveryTerms,
      ModeOfTransport: poHeader.ModeOfTransport,
      ShipVia: poHeader.ShipVia,
      Email: poHeader.Email,
      Phone: poHeader.Phone,
      Attn: poHeader.Attn,
      Currency: poHeader.Currency || poHeader.Supplier?.Currency || "PHP",
      Comments: poHeader.Comments,
      Status: "Pending",
      items: poHeader.items.map((i) => ({
        ItemID: i.ItemId,
        CatalogNo: i.CatalogNo,
        ItemName: i.Description,
        OrderedQty: i.OrderedQty,
        UnitPrice: i.UnitPrice,
      })),
    };

    try {
      await apiService.addPurchaseOrder(payload);

      alert("Purchase Order saved successfully!");

      const poData = await apiService.getPurchaseOrders();

      const merged = (poData || []).map((po) => {
        const supplier = suppliers.find(
          (s) => Number(s.SupplierID) === Number(po.SupplierID)
        );

        return {
          ...po,
          SupplierName:
            po.SupplierName || supplier?.SupplierName || supplier?.Name || "",
        };
      });

      setPoList(merged);
      setPoPage(1);
      setShowCreateModal(false);

      await resetPoForm();
    } catch (error) {
      console.error("Error saving PO:", error);
      alert("Failed to save PO. Check console for details.");
    }
  };

  const downloadPDF = async () => {
    const viewContent = document.querySelector(".po-view-content");

    if (!viewContent) {
      alert("PO preview not found.");
      return;
    }

    const pageElements = Array.from(
      viewContent.querySelectorAll(".po-document .po-page")
    );

    if (!pageElements.length) {
      alert("No PO pages found. Please check PoPrintTemplate page structure.");
      return;
    }

    const originalViewStyle = {
      maxHeight: viewContent.style.maxHeight,
      overflow: viewContent.style.overflow,
      height: viewContent.style.height,
    };

    const printElements = pageElements
      .map((page) => page.querySelector(".po-print"))
      .filter(Boolean);

    try {
      viewContent.style.maxHeight = "none";
      viewContent.style.overflow = "visible";
      viewContent.style.height = "auto";

      printElements.forEach((el) => el.classList.add("pdf-mode"));

      await new Promise((resolve) => {
        requestAnimationFrame(() => {
          requestAnimationFrame(resolve);
        });
      });

      const pdf = new jsPDF("p", "mm", "a4");

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const margin = 8;
      const usableWidth = pdfWidth - margin * 2;
      const usableHeight = pdfHeight - margin * 2;

      for (let i = 0; i < pageElements.length; i++) {
        const page = pageElements[i];

        const canvas = await html2canvas(page, {
          scale: 2,
          useCORS: true,
          backgroundColor: "#ffffff",
          logging: false,
          scrollX: 0,
          scrollY: 0,
          windowWidth: document.documentElement.scrollWidth,
          windowHeight: document.documentElement.scrollHeight,
        });

        const imgData = canvas.toDataURL("image/png");

        let imgWidth = usableWidth;
        let imgHeight = (canvas.height * imgWidth) / canvas.width;

        if (imgHeight > usableHeight) {
          imgHeight = usableHeight;
          imgWidth = (canvas.width * imgHeight) / canvas.height;
        }

        const x = (pdfWidth - imgWidth) / 2;
        const y = margin;

        if (i > 0) {
          pdf.addPage();
        }

        pdf.addImage(imgData, "PNG", x, y, imgWidth, imgHeight);
      }

      const fileName = selectedPO?.PONumber
        ? `${selectedPO.PONumber}.pdf`
        : "PurchaseOrder.pdf";

      pdf.save(fileName);
    } catch (error) {
      console.error("Failed to generate PDF:", error);
      alert("Failed to generate PDF.");
    } finally {
      printElements.forEach((el) => el.classList.remove("pdf-mode"));

      viewContent.style.maxHeight = originalViewStyle.maxHeight;
      viewContent.style.overflow = originalViewStyle.overflow;
      viewContent.style.height = originalViewStyle.height;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";

    const options = {
      year: "numeric",
      month: "long",
      day: "numeric",
    };

    return new Date(dateStr).toLocaleDateString("en-US", options);
  };

  const handleViewPO = async (poid) => {
    try {
      const fullPO = await apiService.getPurchaseOrder(poid);
      setSelectedPO(fullPO);
    } catch (error) {
      console.error("Failed to load full PO details:", error);
      alert("Failed to load full PO details.");
    }
  };

  return (
    <div className="po-container">
      <div className="po-topbar">
        <h1>Purchase Orders</h1>

        <button type="button" className="btn-add-po" onClick={openCreateModal}>
          + Create PO
        </button>
      </div>

      <h3 style={{ marginTop: "20px" }}>Saved POs</h3>

      <table className="po-table">
        <thead>
          <tr>
            <th>PO No</th>
            <th>PO Date</th>
            <th>PR No</th>
            <th>PR Date</th>
            <th>Supplier</th>
            <th>Request Delivery</th>
            <th>Status</th>
            <th style={{ width: "120px" }}>Action</th>
          </tr>
        </thead>

        <tbody>
          {pagedPOs.length === 0 ? (
            <tr>
              <td colSpan="8" style={{ textAlign: "center" }}>
                No POs yet
              </td>
            </tr>
          ) : (
            pagedPOs.map((po) => (
              <tr key={po.POID}>
                <td>{po.PONumber || "-"}</td>
                <td>{po.OrderDate ? formatDate(po.OrderDate) : "-"}</td>
                <td>{po.PRNo || "-"}</td>
                <td>{po.PRDate ? formatDate(po.PRDate) : "-"}</td>
                <td>{po.SupplierName || po.Supplier?.SupplierName || "-"}</td>
                <td>
                  {po.RequestDeliveryDate
                    ? formatDate(po.RequestDeliveryDate)
                    : "-"}
                </td>
                <td>
                  <span
                    className={`po-status po-status-${(
                      po.Status || ""
                    ).toLowerCase()}`}
                  >
                    {po.Status || "-"}
                  </span>
                </td>
                <td>
                  <button
                    type="button"
                    className="btn-view-po"
                    onClick={() => handleViewPO(po.POID)}
                  >
                    View
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <div className="po-pagination">
        <button
          type="button"
          onClick={() => setPoPage((p) => Math.max(1, p - 1))}
          disabled={poPage <= 1}
        >
          Prev
        </button>

        <span className="po-page-info">
          Page {poPage} of {totalPages}
        </span>

        <button
          type="button"
          onClick={() => setPoPage((p) => Math.min(totalPages, p + 1))}
          disabled={poPage >= totalPages}
        >
          Next
        </button>
      </div>

      {showCreateModal && (
        <div className="po-modal-overlay">
          <div className="po-modal">
            <div className="po-modal-header">
              <div>
                <h2>Create Purchase Order</h2>
                <p>Fill out the purchase order details and add items below.</p>
              </div>

              <button
                type="button"
                className="po-modal-close"
                onClick={closeCreateModal}
              >
                ✕
              </button>
            </div>

            <form className="po-form" onSubmit={savePO}>
              <div className="po-card">
                <h3>PO Information</h3>

                <div className="po-row">
                  <div className="po-field">
                    <label>PO No</label>
                    <input type="text" value={poHeader.PONumber || ""} readOnly />
                  </div>

                  <div className="po-field">
                    <label>PO Date</label>
                    <input
                      type="date"
                      name="OrderDate"
                      value={poHeader.OrderDate}
                      onChange={(e) =>
                        setPoHeader((prev) => ({
                          ...prev,
                          [e.target.name]: e.target.value,
                        }))
                      }
                      required
                    />
                  </div>

                  <div className="po-field">
                    <label>PR No</label>
                    <input
                      type="text"
                      name="PRNo"
                      value={poHeader.PRNo}
                      onChange={(e) =>
                        setPoHeader((prev) => ({
                          ...prev,
                          PRNo: e.target.value,
                        }))
                      }
                      placeholder="Enter PR number"
                    />
                  </div>

                  <div className="po-field">
                    <label>PR Date</label>
                    <input
                      type="date"
                      name="PRDate"
                      value={poHeader.PRDate}
                      onChange={(e) =>
                        setPoHeader((prev) => ({
                          ...prev,
                          PRDate: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>

                <div className="po-row">
                  <div className="po-field">
                    <label>Supplier</label>
                    <Select
                      classNamePrefix="po-select"
                      options={supplierOptions}
                      value={
                        supplierOptions.find(
                          (opt) =>
                            Number(opt.value) ===
                            Number(poHeader.Supplier?.SupplierID)
                        ) || null
                      }
                      onChange={handleSupplierChange}
                      placeholder="Search supplier..."
                      isSearchable
                      isClearable
                    />
                  </div>

                  <div className="po-field">
                    <label>Request Delivery Date</label>
                    <input
                      type="date"
                      name="RequestDeliveryDate"
                      value={poHeader.RequestDeliveryDate}
                      onChange={(e) =>
                        setPoHeader((prev) => ({
                          ...prev,
                          [e.target.name]: e.target.value,
                        }))
                      }
                      required
                    />
                  </div>

                  <div className="po-field">
                    <label>Email</label>
                    <input
                      type="email"
                      value={poHeader.Email}
                      onChange={(e) =>
                        setPoHeader((prev) => ({
                          ...prev,
                          Email: e.target.value,
                        }))
                      }
                    />
                  </div>

                  <div className="po-field">
                    <label>Phone</label>
                    <input
                      type="text"
                      value={poHeader.Phone}
                      onChange={(e) =>
                        setPoHeader((prev) => ({
                          ...prev,
                          Phone: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>

                <div className="po-row">
                  <div className="po-field">
                    <label>Attn</label>
                    <input
                      type="text"
                      value={poHeader.Attn}
                      onChange={(e) =>
                        setPoHeader((prev) => ({
                          ...prev,
                          Attn: e.target.value,
                        }))
                      }
                    />
                  </div>

                  <div className="po-field">
                    <label>Payment Terms</label>
                    <CreatableSelect
                      classNamePrefix="po-select"
                      options={paymentTermOptions}
                      value={
                        poHeader.PaymentTerms
                          ? {
                              value: poHeader.PaymentTerms,
                              label: poHeader.PaymentTerms,
                            }
                          : null
                      }
                      onChange={(selected) =>
                        setPoHeader((prev) => ({
                          ...prev,
                          PaymentTerms: selected ? selected.value : "",
                        }))
                      }
                      placeholder="Select or type payment terms..."
                      isClearable
                    />
                  </div>

                  <div className="po-field">
                    <label>Delivery Terms</label>
                    <CreatableSelect
                      classNamePrefix="po-select"
                      options={deliveryTermOptions}
                      value={
                        poHeader.DeliveryTerms
                          ? {
                              value: poHeader.DeliveryTerms,
                              label: poHeader.DeliveryTerms,
                            }
                          : null
                      }
                      onChange={(selected) =>
                        setPoHeader((prev) => ({
                          ...prev,
                          DeliveryTerms: selected ? selected.value : "",
                        }))
                      }
                      placeholder="Select or type delivery terms..."
                      isClearable
                    />
                  </div>

                  <div className="po-field">
                    <label>Mode of Transport</label>
                    <CreatableSelect
                      classNamePrefix="po-select"
                      options={modeOfTransportOptions}
                      value={
                        poHeader.ModeOfTransport
                          ? {
                              value: poHeader.ModeOfTransport,
                              label: poHeader.ModeOfTransport,
                            }
                          : null
                      }
                      onChange={(selected) =>
                        setPoHeader((prev) => ({
                          ...prev,
                          ModeOfTransport: selected ? selected.value : "",
                        }))
                      }
                      placeholder="Select or type mode of transport..."
                      isClearable
                    />
                  </div>
                </div>

                <div className="po-row">
                  <div className="po-field">
                    <label>Ship Via</label>
                    <CreatableSelect
                      classNamePrefix="po-select"
                      options={shipViaOptions}
                      value={
                        poHeader.ShipVia
                          ? {
                              value: poHeader.ShipVia,
                              label: poHeader.ShipVia,
                            }
                          : null
                      }
                      onChange={(selected) =>
                        setPoHeader((prev) => ({
                          ...prev,
                          ShipVia: selected ? selected.value : "",
                        }))
                      }
                      placeholder="Select or type ship via..."
                      isClearable
                    />
                  </div>

                  <div className="po-field po-field-full">
                    <label>Purchase Order Comments</label>
                    <textarea
                      rows="3"
                      value={poHeader.Comments}
                      onChange={(e) =>
                        setPoHeader((prev) => ({
                          ...prev,
                          Comments: e.target.value,
                        }))
                      }
                      placeholder="Enter special instructions..."
                    />
                  </div>
                </div>
              </div>

              <div className="po-card">
                <h3>Add Items</h3>

                <div className="po-item-form">
                  <input
                    type="text"
                    className="po-search"
                    placeholder="Search Catalog No or Description..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />

                  <select value={itemForm.ItemId} onChange={handleItemSelect}>
                    <option value="">Select Item</option>

                    {filteredItems.map((it) => {
                      const itemId = it.ItemId || it.ItemID;

                      return (
                        <option key={itemId} value={itemId}>
                          {it.CatalogNo} - {it.ItemName || it.Description}
                        </option>
                      );
                    })}
                  </select>

                  <input
                    type="number"
                    name="OrderedQty"
                    placeholder="Ordered Qty"
                    value={itemForm.OrderedQty}
                    onChange={handleQtyChange}
                  />

                  <input
                    type="number"
                    name="UnitPrice"
                    placeholder="Unit Price"
                    value={itemForm.UnitPrice}
                    onChange={handleQtyChange}
                    step="0.01"
                  />

                  <button
                    type="button"
                    className="btn-add-item"
                    onClick={addLineItem}
                  >
                    {editIndex !== null ? "Update Item" : "Add Item"}
                  </button>
                </div>

                <div className="po-table-wrap">
                  <table className="po-table">
                    <thead>
                      <tr>
                        <th>PODetailID</th>
                        <th>Catalog No</th>
                        <th>Description</th>
                        <th>Ordered Qty</th>
                        <th>Unit Price</th>
                        <th>Extended Price</th>
                        <th>Received Qty</th>
                        <th>Pending Qty</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>

                    <tbody>
                      {poHeader.items.length === 0 ? (
                        <tr>
                          <td colSpan="10" style={{ textAlign: "center" }}>
                            No items yet
                          </td>
                        </tr>
                      ) : (
                        poHeader.items.map((item, index) => (
                          <tr key={item.PODetailID}>
                            <td>{item.PODetailID}</td>
                            <td>{item.CatalogNo}</td>
                            <td>{item.Description}</td>
                            <td>{item.OrderedQty}</td>
                            <td>{Number(item.UnitPrice || 0).toFixed(2)}</td>
                            <td>{Number(item.ExtendedPrice || 0).toFixed(2)}</td>
                            <td>{item.ReceivedQty}</td>
                            <td>{item.PendingQty}</td>
                            <td>{item.Status}</td>
                            <td>
                              <div className="po-action-group">
                                <button
                                  type="button"
                                  className="btn-edit-row"
                                  onClick={() => editLineItem(index)}
                                >
                                  Edit
                                </button>

                                <button
                                  type="button"
                                  className="btn-delete-row"
                                  onClick={() => deleteLineItem(index)}
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>

                    {poHeader.items.length > 0 && (
                      <tfoot>
                        <tr>
                          <td colSpan="5" style={{ textAlign: "right" }}>
                            <strong>Total:</strong>
                          </td>
                          <td colSpan="5">
                            <strong>
                              {formatCurrencyTotal(
                                poHeader.items.reduce(
                                  (sum, i) => sum + Number(i.ExtendedPrice || 0),
                                  0
                                )
                              )}
                            </strong>
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>

              <div className="po-modal-actions">
                <button
                  type="button"
                  className="btn-cancel-po"
                  onClick={closeCreateModal}
                >
                  Cancel
                </button>

                <button type="submit" className="btn-save">
                  Save PO
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {selectedPO && (
        <div className="po-modal-overlay po-view-overlay" onClick={closeViewModal}>
          <div
            className="po-modal po-view-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="po-modal-header">
              <div>
                <h2>Purchase Order Preview</h2>
                <p>Review the purchase order details below.</p>
              </div>

              <button
                type="button"
                className="po-modal-close"
                onClick={closeViewModal}
              >
                ✕
              </button>
            </div>

            <div className="po-view-content">
              <PoPrintTemplate po={selectedPO} pdfMode={false} />
            </div>

            <div className="po-modal-actions">
              <button
                type="button"
                className="btn-cancel-po"
                onClick={closeViewModal}
              >
                Close
              </button>

              <button
                type="button"
                className="btn-download"
                onClick={downloadPDF}
              >
                Download PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}